import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { Button, TextInput, Alert } from 'flowbite-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { ChevronLeft } from 'lucide-react';
import { sendPasswordResetWithCustomRedirect } from '../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { isDarkMode } = useDarkMode();

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      await sendPasswordResetWithCustomRedirect(email);
      setResetEmailSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;

  return (
    <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {isNative ? (
        <>
          {/* Background Image with Gradient Overlay - Native App */}
          <div className="absolute inset-0">
            <img
              src="/images/headerbg.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className={`absolute inset-0 ${
              isDarkMode
                ? 'bg-gradient-to-b from-emerald-900/95 via-[#111111]/98 to-[#111111]'
                : 'bg-gradient-to-b from-emerald-600/95 via-white/98 to-white'
            }`} />
          </div>

          {/* Gradient Orbs - Native App */}
          <div className={`absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-200/40'
          }`} />
          <div className={`absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-100/60'
          }`} />
        </>
      ) : (
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
            : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
        }`} />
      )}

      {/* Back Button */}
      <div className="absolute top-0 left-0 z-10 m-4">
        <Link
          to="/login"
          className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
            isDarkMode 
              ? 'bg-white/10 hover:bg-white/20 text-white' 
              : 'bg-black/10 hover:bg-black/20 text-black'
          }`}
        >
          <ChevronLeft size={20} />
        </Link>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4" 
           style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top) + 2rem)' : '2rem' }}>
        <div className={`w-full max-w-md ${
          isNative ? 'bg-transparent' : `rounded-[24px] overflow-hidden ${
            isDarkMode 
              ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
              : 'bg-white shadow-sm border border-gray-200'
          }`
        }`}>
          <div className="p-8">
            {/* Logo - Only show in native app */}
            {isNative && (
              <div className="flex justify-center mb-12">
                <div className="w-24 h-24">
                  <img
                    src="/images/ummahti_white_transparent.png"
                    alt="Ummahti"
                    className={`w-full transition-opacity duration-300 ${isDarkMode ? 'opacity-90' : 'opacity-80'}`}
                  />
                </div>
              </div>
            )}

            <h2 className={`text-2xl font-light mb-4 text-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Reset your password
            </h2>
            
            <p className={`text-center mb-8 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Enter your email address and we'll send you a link to reset your password
            </p>
            
            {error && (
              <Alert 
                color="failure" 
                className="mb-6"
                onDismiss={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            
            {resetEmailSent ? (
              <div className="text-center">
                <Alert 
                  color="success" 
                  className="mb-6"
                >
                  Password reset email sent. Please check your inbox.
                </Alert>
                <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Didn't receive an email? Check your spam folder or try again.
                </p>
                <div className="mt-6">
                  <Link to="/login">
                    <Button 
                      color="light"
                      className={`w-full ${
                        isNative ? 'py-4 px-6 rounded-2xl backdrop-blur-md' : ''
                      } ${
                        isDarkMode 
                          ? 'bg-white/10 hover:bg-white/20 text-white border border-white/[0.05]' 
                          : 'bg-black/10 hover:bg-black/20 text-gray-900 border border-black/[0.05]'
                      }`}
                    >
                      Back to login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <TextInput
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`${isDarkMode ? '[&>input]:bg-[#1A1A1A] [&>input]:border-gray-800' : ''} ${
                      isNative ? '[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md' : ''
                    }`}
                  />
                </div>
                <Button 
                  type="submit" 
                  className={`w-full ${
                    isNative ? 'py-4 px-6 rounded-2xl backdrop-blur-md' : ''
                  } ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </Button>
                <div className="mt-4 text-center">
                  <Link 
                    to="/login" 
                    className={`text-sm ${
                      isDarkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-500'
                    }`}
                  >
                    Back to login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer Text - Only show in native app */}
      {isNative && (
        <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword; 